import type Joi from "joi";
import type { ValidationResult } from "joi";
import { type ChangeEvent, type Ref, useState } from "react";

import {} from "@/components/icons/icons";
import { HorizontalSpacer } from "@/components/layout/horizontal-spacer";
import { ErrorMessageString } from "@/components/ui/error-message";
import { LocalizedJoi } from "@/utils/localized-joi";
import { classNames } from "@/utils/styling-utils";
import type { InputBaseProps } from "./input.model";
import { SelectWrapper } from "./select-wrapper";

interface SelectProps extends InputBaseProps {
  defaultValue?: string;
  externalValidationError?: string;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  options: string[];
  ref?: Ref<HTMLSelectElement>;
  validationSchema?: Joi.StringSchema;
  value?: string;
  autoComplete?: string;
  fullWidth?: boolean;
}

export const Select = ({
  defaultValue,
  disabled,
  id,
  labelledBy,
  name,
  onBlur,
  onChange,
  options,
  placeholder,
  required,
  externalValidationError,
  validationSchema = required
    ? LocalizedJoi.string().label(name).required()
    : LocalizedJoi.string().label(name).allow(""),
  value,
  ref,
  autoComplete,
  fullWidth,
}: SelectProps) => {
  const [validationError, setValidationError] = useState<ValidationResult<string> | undefined>();
  const error = validationError?.error?.message ?? externalValidationError ?? "";

  return (
    <SelectWrapper>
      <select
        aria-labelledby={labelledBy}
        className={classNames("select", value && "select--selected", fullWidth && "select--width--full")}
        defaultValue={defaultValue ?? value}
        disabled={disabled}
        id={id}
        name={name}
        onBlur={(e) => {
          if (onBlur) {
            onBlur();
          }
          setValidationError(validationSchema.validate(e.target.value));
        }}
        onChange={onChange}
        required={required}
        ref={ref}
        autoComplete={autoComplete}
      >
        {placeholder && (
          <option className="select__option--placeholder" value="">
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option className="select__option" key={option.toString()} value={option}>
            {option}
          </option>
        ))}
      </select>

      {error && (
        <>
          <HorizontalSpacer size="xx-small" />
          <ErrorMessageString errorString={error} />
        </>
      )}
    </SelectWrapper>
  );
};
